// Story 1.20 — TanStack Query hook for GET /api/v1/account/data-export-requests/:id.
//
// Polls every 10 seconds while the export is `pending` or `processing`;
// stops polling once `completed`/`failed`/`expired`. The
// DataExportSection / PortabilityExportSection components feed
// `exportRequestId` from the dsar-summary's `active_request_id` field.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type ExportRequestStatus = {
  export_request_id: string;
  kind: "export" | "portability";
  status: "pending" | "processing" | "completed" | "failed" | "expired";
  download_url: string | null;
  download_url_expires_at: string | null;
  bundle_size_bytes: number | null;
  failure_code: string | null;
  failure_message: string | null;
  completed_at: string | null;
  created_at: string;
};

export type ExportRequestStatusEnvelope = {
  data: ExportRequestStatus;
};

async function fetchExportRequest(
  id: string,
): Promise<ExportRequestStatusEnvelope> {
  const response = await fetch(`/api/v1/account/data-export-requests/${id}`, {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw new Error(`export-request status fetch failed: ${response.status}`);
  }
  return (await response.json()) as ExportRequestStatusEnvelope;
}

export function useExportRequest(id: string | null) {
  return useQuery({
    queryKey: ["export-request", id],
    queryFn: () => fetchExportRequest(id as string),
    enabled: !!id,
    staleTime: 5_000,
    refetchInterval: (query) => {
      const status = query.state.data?.data.status;
      if (status === "pending" || status === "processing") {
        return 10_000;
      }
      return false;
    },
    retry: false,
  });
}
